(() => {
  'use strict';

  /* =========================================================
     SETLARO SHIPPING & RETURNS V2
     ---------------------------------------------------------
     Improvements:
     1. No visible page scrolling after close
     2. Mobile bottom sheet follows finger
     3. Swipe down to close
     4. Snap back when drag distance is insufficient
     5. Overlay fades while dragging
     6. Preserve original page scroll position
     7. Desktop right drawer remains unchanged
     8. ESC / overlay / close button supported
     9. Focus trap + safe focus restoration
  ========================================================== */


  if (window.__setlaroShippingReturnsV2Loaded) {
    return;
  }

  window.__setlaroShippingReturnsV2Loaded = true;


  const MOBILE_QUERY = '(max-width: 749px)';


  const state = {
    layer: null,
    drawer: null,
    trigger: null,

    scrollY: 0,
    bodyStyles: null,

    drag: {
      active: false,

      pointerId: null,
      captureElement: null,

      startX: 0,
      startY: 0,

      currentX: 0,
      currentY: 0,

      lastY: 0,
      lastTime: 0,

      startTime: 0,

      distance: 0
    }
  };


  /* =========================================================
     BASIC HELPERS
  ========================================================== */

  function isMobile() {
    return window
      .matchMedia(MOBILE_QUERY)
      .matches;
  }


  function getLayerFromTrigger(trigger) {
    if (!trigger) {
      return null;
    }

    const id =
      trigger.getAttribute(
        'data-setlaro-srd-open'
      );

    if (!id) {
      return null;
    }

    return document.getElementById(id);
  }


  function getDuration(layer) {
    if (!layer) {
      return 320;
    }

    const value =
      parseInt(
        layer.dataset.duration,
        10
      );

    return Number.isFinite(value)
      ? value
      : 320;
  }



  /* =========================================================
     PORTAL
     ---------------------------------------------------------
     Move layer directly under <body> while open.
     This avoids Minimog parent transform / overflow problems.
  ========================================================== */

  function portalToBody(layer) {
    if (!layer) {
      return;
    }


    if (!layer.__setlaroOriginalParent) {
      layer.__setlaroOriginalParent =
        layer.parentNode;

      layer.__setlaroOriginalNextSibling =
        layer.nextSibling;
    }


    if (layer.__setlaroRestoreTimer) {
      clearTimeout(
        layer.__setlaroRestoreTimer
      );

      layer.__setlaroRestoreTimer = null;
    }


    if (layer.parentNode !== document.body) {
      document.body.appendChild(layer);
    }
  }


  function restorePortal(layer) {
    if (!layer) {
      return;
    }


    const duration =
      getDuration(layer);


    layer.__setlaroRestoreTimer =
      window.setTimeout(() => {

        const parent =
          layer.__setlaroOriginalParent;

        const sibling =
          layer.__setlaroOriginalNextSibling;


        if (
          !parent ||
          !document.documentElement.contains(parent)
        ) {
          layer.__setlaroRestoreTimer = null;
          return;
        }


        if (
          sibling &&
          sibling.parentNode === parent
        ) {
          parent.insertBefore(
            layer,
            sibling
          );
        } else {
          parent.appendChild(layer);
        }


        layer.__setlaroRestoreTimer = null;

      }, duration + 60);
  }



  /* =========================================================
     TEMPORARILY DISABLE SMOOTH SCROLL
     ---------------------------------------------------------
     This fixes:
     close drawer
     → page appears to scroll from top back to product area
  ========================================================== */

  function enableInstantScrollMode() {
    const html =
      document.documentElement;

    const body =
      document.body;


    const previous = {
      htmlValue:
        html.style.getPropertyValue(
          'scroll-behavior'
        ),

      htmlPriority:
        html.style.getPropertyPriority(
          'scroll-behavior'
        ),

      bodyValue:
        body.style.getPropertyValue(
          'scroll-behavior'
        ),

      bodyPriority:
        body.style.getPropertyPriority(
          'scroll-behavior'
        )
    };


    /*
     * !important deliberately used here because
     * some themes declare smooth scrolling globally.
     */

    html.style.setProperty(
      'scroll-behavior',
      'auto',
      'important'
    );

    body.style.setProperty(
      'scroll-behavior',
      'auto',
      'important'
    );


    return function restoreScrollBehavior() {

      if (previous.htmlValue) {
        html.style.setProperty(
          'scroll-behavior',
          previous.htmlValue,
          previous.htmlPriority
        );
      } else {
        html.style.removeProperty(
          'scroll-behavior'
        );
      }


      if (previous.bodyValue) {
        body.style.setProperty(
          'scroll-behavior',
          previous.bodyValue,
          previous.bodyPriority
        );
      } else {
        body.style.removeProperty(
          'scroll-behavior'
        );
      }

    };
  }



  /* =========================================================
     PAGE SCROLL LOCK
  ========================================================== */

  function lockPageScroll() {
    const body =
      document.body;


    state.scrollY =
      window.scrollY ||
      window.pageYOffset ||
      0;


    state.bodyStyles = {
      position:
        body.style.position,

      top:
        body.style.top,

      left:
        body.style.left,

      right:
        body.style.right,

      width:
        body.style.width,

      overflow:
        body.style.overflow,

      paddingRight:
        body.style.paddingRight
    };


    const scrollbarWidth =
      Math.max(
        0,
        window.innerWidth -
        document.documentElement.clientWidth
      );


    const currentPadding =
      parseFloat(
        window
          .getComputedStyle(body)
          .paddingRight
      ) || 0;


    document.documentElement
      .classList
      .add('setlaro-srd-open');


    body.classList.add(
      'setlaro-srd-open'
    );


    /*
     * position: fixed is more reliable than just
     * overflow:hidden on iOS.
     */

    body.style.position =
      'fixed';

    body.style.top =
      `-${state.scrollY}px`;

    body.style.left =
      '0';

    body.style.right =
      '0';

    body.style.width =
      '100%';

    body.style.overflow =
      'hidden';


    if (scrollbarWidth > 0) {
      body.style.paddingRight =
        `${currentPadding + scrollbarWidth}px`;
    }
  }



  /* =========================================================
     RESTORE PAGE WITHOUT ANY SCROLL ANIMATION
  ========================================================== */

  function unlockPageScroll() {
    const body =
      document.body;

    const html =
      document.documentElement;

    const restoreY =
      state.scrollY;


    const restoreScrollBehavior =
      enableInstantScrollMode();


    document.documentElement
      .classList
      .remove('setlaro-srd-open');


    body.classList.remove(
      'setlaro-srd-open'
    );


    if (state.bodyStyles) {
      body.style.position =
        state.bodyStyles.position;

      body.style.top =
        state.bodyStyles.top;

      body.style.left =
        state.bodyStyles.left;

      body.style.right =
        state.bodyStyles.right;

      body.style.width =
        state.bodyStyles.width;

      body.style.overflow =
        state.bodyStyles.overflow;

      body.style.paddingRight =
        state.bodyStyles.paddingRight;
    }


    /*
     * Restore using all common scrolling roots.
     * This is intentionally immediate.
     */

    html.scrollTop =
      restoreY;

    body.scrollTop =
      restoreY;

    window.scrollTo(
      0,
      restoreY
    );


    /*
     * Some mobile browsers settle the body layout
     * one frame later, so reinforce the position.
     */

    requestAnimationFrame(() => {

      html.scrollTop =
        restoreY;

      body.scrollTop =
        restoreY;

      window.scrollTo(
        0,
        restoreY
      );


      requestAnimationFrame(() => {
        restoreScrollBehavior();
      });

    });


    state.bodyStyles = null;


    return restoreY;
  }



  /* =========================================================
     SAFE FOCUS
     ---------------------------------------------------------
     preventScroll is supported by modern browsers,
     but we also guard against themes/browser webviews
     that still move the page when focus changes.
  ========================================================== */

  function focusWithoutScrolling(
    element,
    restoreY
  ) {
    if (
      !element ||
      !document.documentElement.contains(element)
    ) {
      return;
    }


    const html =
      document.documentElement;

    const body =
      document.body;


    const targetY =
      Number.isFinite(restoreY)
        ? restoreY
        : (
            window.scrollY ||
            window.pageYOffset ||
            0
          );


    const restoreScrollBehavior =
      enableInstantScrollMode();


    try {
      element.focus({
        preventScroll: true
      });
    } catch (error) {
      element.focus();
    }


    html.scrollTop =
      targetY;

    body.scrollTop =
      targetY;

    window.scrollTo(
      0,
      targetY
    );


    requestAnimationFrame(() => {

      html.scrollTop =
        targetY;

      body.scrollTop =
        targetY;

      window.scrollTo(
        0,
        targetY
      );


      requestAnimationFrame(() => {
        restoreScrollBehavior();
      });

    });
  }



  /* =========================================================
     FOCUSABLE ELEMENTS
  ========================================================== */

  function getFocusableElements(drawer) {
    if (!drawer) {
      return [];
    }


    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');


    return Array
      .from(
        drawer.querySelectorAll(selector)
      )
      .filter(element => {

        const style =
          window.getComputedStyle(element);


        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden'
        );

      });
  }



  /* =========================================================
     DRAG HELPERS
  ========================================================== */

  function clearDragResetTimer(drawer) {
    if (
      drawer &&
      drawer.__setlaroDragResetTimer
    ) {
      clearTimeout(
        drawer.__setlaroDragResetTimer
      );

      drawer.__setlaroDragResetTimer =
        null;
    }
  }


  function resetDragState() {
    state.drag.active =
      false;

    state.drag.pointerId =
      null;

    state.drag.captureElement =
      null;

    state.drag.startX =
      0;

    state.drag.startY =
      0;

    state.drag.currentX =
      0;

    state.drag.currentY =
      0;

    state.drag.lastY =
      0;

    state.drag.lastTime =
      0;

    state.drag.startTime =
      0;

    state.drag.distance =
      0;
  }


  function cleanDragStyles(
    drawer,
    overlay
  ) {
    if (drawer) {
      clearDragResetTimer(drawer);

      drawer.classList.remove(
        'is-dragging'
      );

      drawer.style.removeProperty(
        '--setlaro-srd-drag-y'
      );
    }


    if (overlay) {
      overlay.style.removeProperty(
        'opacity'
      );
    }
  }



  /* =========================================================
     OPEN DRAWER
  ========================================================== */

  function openDrawer(trigger) {
    const layer =
      getLayerFromTrigger(trigger);


    if (!layer) {
      console.warn(
        '[SETLARO] Shipping & Returns layer not found.'
      );

      return;
    }


    if (
      state.layer === layer &&
      layer.classList.contains('is-open')
    ) {
      return;
    }


    portalToBody(layer);


    const drawer =
      layer.querySelector(
        '.setlaro-srd-drawer'
      );


    if (!drawer) {
      return;
    }


    const overlay =
      layer.querySelector(
        '.setlaro-srd-overlay'
      );


    cleanDragStyles(
      drawer,
      overlay
    );


    resetDragState();


    state.layer =
      layer;

    state.drawer =
      drawer;

    state.trigger =
      trigger;


    lockPageScroll();


    layer.setAttribute(
      'aria-hidden',
      'false'
    );


    trigger.setAttribute(
      'aria-expanded',
      'true'
    );


    const drawerBody =
      layer.querySelector(
        '.setlaro-srd-body'
      );


    if (drawerBody) {
      drawerBody.scrollTop = 0;
    }


    requestAnimationFrame(() => {

      requestAnimationFrame(() => {

        layer.classList.add(
          'is-open'
        );

      });

    });


    const closeButton =
      layer.querySelector(
        '.setlaro-srd-close'
      );


    if (closeButton) {
      window.setTimeout(() => {

        try {
          closeButton.focus({
            preventScroll: true
          });
        } catch (error) {
          closeButton.focus();
        }

      }, 50);
    }


    document.dispatchEvent(
      new CustomEvent(
        'setlaro:shipping-returns:open',
        {
          detail: {
            layer,
            drawer
          }
        }
      )
    );
  }



  /* =========================================================
     CLOSE DRAWER
  ========================================================== */

  function closeDrawer(
    returnFocus = true,
    fromDrag = false
  ) {
    const layer =
      state.layer;

    const drawer =
      state.drawer;

    const trigger =
      state.trigger;


    if (!layer) {
      return;
    }


    const overlay =
      layer.querySelector(
        '.setlaro-srd-overlay'
      );


    const duration =
      getDuration(layer);


    const restoreY =
      state.scrollY;


    /*
     * If closing from a finger drag,
     * keep current translateY as animation start point.
     */

    if (drawer) {
      drawer.classList.remove(
        'is-dragging'
      );
    }


    if (fromDrag && overlay) {
      /*
       * Fade from current opacity to 0,
       * not back to full opacity first.
       */
      overlay.style.opacity = '0';
    } else {
      cleanDragStyles(
        drawer,
        overlay
      );
    }


    layer.classList.remove(
      'is-open'
    );


    layer.setAttribute(
      'aria-hidden',
      'true'
    );


    if (trigger) {
      trigger.setAttribute(
        'aria-expanded',
        'false'
      );
    }


    /*
     * Critical fix:
     * restore page with smooth scrolling disabled.
     */

    unlockPageScroll();


    restorePortal(layer);


    /*
     * Keep drag offset until closing transition
     * finishes, then clean it.
     */

    if (
      fromDrag &&
      drawer
    ) {
      drawer.__setlaroDragResetTimer =
        window.setTimeout(() => {

          cleanDragStyles(
            drawer,
            overlay
          );

        }, duration + 80);
    }


    resetDragState();


    state.layer =
      null;

    state.drawer =
      null;

    state.trigger =
      null;


    /*
     * Restore focus without ever moving the page.
     */

    if (
      returnFocus &&
      trigger &&
      document.documentElement.contains(trigger)
    ) {
      window.setTimeout(() => {

        focusWithoutScrolling(
          trigger,
          restoreY
        );

      }, 60);
    }


    document.dispatchEvent(
      new CustomEvent(
        'setlaro:shipping-returns:close',
        {
          detail: {
            layer
          }
        }
      )
    );
  }



  /* =========================================================
     SNAP DRAWER BACK AFTER INCOMPLETE DRAG
  ========================================================== */

  function snapDrawerBack() {
    const drawer =
      state.drawer;

    const layer =
      state.layer;


    if (
      !drawer ||
      !layer
    ) {
      resetDragState();
      return;
    }


    const overlay =
      layer.querySelector(
        '.setlaro-srd-overlay'
      );


    drawer.classList.remove(
      'is-dragging'
    );


    /*
     * Transition from current position back to 0.
     */

    drawer.style.setProperty(
      '--setlaro-srd-drag-y',
      '0px'
    );


    if (overlay) {
      overlay.style.removeProperty(
        'opacity'
      );
    }


    const duration =
      getDuration(layer);


    clearDragResetTimer(drawer);


    drawer.__setlaroDragResetTimer =
      window.setTimeout(() => {

        drawer.style.removeProperty(
          '--setlaro-srd-drag-y'
        );

        drawer.__setlaroDragResetTimer =
          null;

      }, duration + 50);


    resetDragState();
  }



  /* =========================================================
     START MOBILE DRAG
     ---------------------------------------------------------
     Entire top header can be dragged,
     except buttons/links.
  ========================================================== */

  function handlePointerDown(event) {
    if (
      !state.layer ||
      !state.drawer ||
      !isMobile()
    ) {
      return;
    }


    if (
      event.isPrimary === false
    ) {
      return;
    }


    const header =
      event.target.closest(
        '.setlaro-srd-header'
      );


    if (
      !header ||
      !state.drawer.contains(header)
    ) {
      return;
    }


    /*
     * Clicking X should close normally,
     * not start a drag.
     */

    if (
      event.target.closest(
        'button, a, input, select, textarea'
      )
    ) {
      return;
    }


    clearDragResetTimer(
      state.drawer
    );


    state.drag.active =
      true;

    state.drag.pointerId =
      event.pointerId;

    state.drag.captureElement =
      header;

    state.drag.startX =
      event.clientX;

    state.drag.startY =
      event.clientY;

    state.drag.currentX =
      event.clientX;

    state.drag.currentY =
      event.clientY;

    state.drag.lastY =
      event.clientY;

    state.drag.lastTime =
      performance.now();

    state.drag.startTime =
      state.drag.lastTime;

    state.drag.distance =
      0;


    state.drawer.classList.add(
      'is-dragging'
    );


    try {
      header.setPointerCapture(
        event.pointerId
      );
    } catch (error) {
      /* Ignore unsupported capture */
    }


    event.preventDefault();
  }



  /* =========================================================
     DRAG MOVE
  ========================================================== */

  function handlePointerMove(event) {
    if (
      !state.drag.active ||
      !state.drawer ||
      !state.layer ||
      event.pointerId !==
        state.drag.pointerId
    ) {
      return;
    }


    const rawDistance =
      event.clientY -
      state.drag.startY;


    /*
     * Only downward drag counts.
     */

    let dragY =
      Math.max(
        0,
        rawDistance
      );


    const drawerHeight =
      state.drawer
        .getBoundingClientRect()
        .height;


    /*
     * Do not allow dragging beyond full sheet height.
     */

    dragY =
      Math.min(
        dragY,
        drawerHeight
      );


    state.drag.lastY =
      state.drag.currentY;

    state.drag.lastTime =
      performance.now();

    state.drag.currentX =
      event.clientX;

    state.drag.currentY =
      event.clientY;

    state.drag.distance =
      dragY;


    state.drawer.style.setProperty(
      '--setlaro-srd-drag-y',
      `${dragY}px`
    );


    /*
     * Fade overlay proportionally while sheet
     * follows the finger.
     */

    const overlay =
      state.layer.querySelector(
        '.setlaro-srd-overlay'
      );


    if (overlay) {
      const fadeDistance =
        Math.max(
          180,
          drawerHeight * 0.65
        );


      const progress =
        Math.min(
          dragY / fadeDistance,
          1
        );


      overlay.style.opacity =
        String(
          Math.max(
            0,
            1 - progress
          )
        );
    }


    event.preventDefault();
  }



  /* =========================================================
     FINISH MOBILE DRAG
  ========================================================== */

  function handlePointerUp(event) {
    if (
      !state.drag.active ||
      !state.drawer ||
      event.pointerId !==
        state.drag.pointerId
    ) {
      return;
    }


    const drawer =
      state.drawer;


    const now =
      performance.now();


    const recentElapsed =
      Math.max(
        1,
        now -
        state.drag.lastTime
      );


    const recentDistance =
      event.clientY -
      state.drag.lastY;


    const velocity =
      recentDistance /
      recentElapsed;


    const drawerHeight =
      drawer
        .getBoundingClientRect()
        .height;


    /*
     * Distance threshold:
     * usually around 90–140px depending on sheet height.
     */

    const closeThreshold =
      Math.min(
        140,
        Math.max(
          90,
          drawerHeight * 0.18
        )
      );


    /*
     * Two ways to close:
     *
     * 1. Drag far enough
     * 2. Quick downward flick
     */

    const draggedFarEnough =
      state.drag.distance >=
      closeThreshold;


    const fastDownwardFlick =
      (
        velocity > 0.55 &&
        state.drag.distance > 35
      );


    const captureElement =
      state.drag.captureElement;


    try {
      if (
        captureElement &&
        captureElement.hasPointerCapture(
          event.pointerId
        )
      ) {
        captureElement.releasePointerCapture(
          event.pointerId
        );
      }
    } catch (error) {
      /* Ignore */
    }


    if (
      draggedFarEnough ||
      fastDownwardFlick
    ) {
      closeDrawer(
        true,
        true
      );
    } else {
      snapDrawerBack();
    }
  }



  /* =========================================================
     POINTER CANCEL
  ========================================================== */

  function handlePointerCancel(event) {
    if (
      !state.drag.active ||
      event.pointerId !==
        state.drag.pointerId
    ) {
      return;
    }


    snapDrawerBack();
  }



  /* =========================================================
     CLICK EVENTS
  ========================================================== */

  document.addEventListener(
    'click',
    event => {

      /* -------------------------
         OPEN
      -------------------------- */

      const trigger =
        event.target.closest(
          '[data-setlaro-srd-open]'
        );


      if (trigger) {
        event.preventDefault();

        openDrawer(trigger);

        return;
      }


      /* -------------------------
         CLOSE
         X / Overlay
      -------------------------- */

      const close =
        event.target.closest(
          '[data-setlaro-srd-close]'
        );


      if (
        close &&
        state.layer
      ) {
        event.preventDefault();

        closeDrawer(
          true,
          false
        );
      }

    }
  );



  /* =========================================================
     POINTER EVENTS
  ========================================================== */

  document.addEventListener(
    'pointerdown',
    handlePointerDown,
    {
      passive: false
    }
  );


  document.addEventListener(
    'pointermove',
    handlePointerMove,
    {
      passive: false
    }
  );


  document.addEventListener(
    'pointerup',
    handlePointerUp,
    {
      passive: true
    }
  );


  document.addEventListener(
    'pointercancel',
    handlePointerCancel,
    {
      passive: true
    }
  );



  /* =========================================================
     KEYBOARD
  ========================================================== */

  document.addEventListener(
    'keydown',
    event => {

      if (
        !state.layer ||
        !state.drawer
      ) {
        return;
      }


      /* ESC */

      if (event.key === 'Escape') {
        event.preventDefault();

        closeDrawer();

        return;
      }


      /* Focus trap */

      if (event.key !== 'Tab') {
        return;
      }


      const focusable =
        getFocusableElements(
          state.drawer
        );


      if (!focusable.length) {
        return;
      }


      const first =
        focusable[0];

      const last =
        focusable[
          focusable.length - 1
        ];


      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();

        last.focus();

        return;
      }


      if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();

        first.focus();
      }

    }
  );



  /* =========================================================
     SHOPIFY THEME EDITOR SUPPORT
  ========================================================== */

  document.addEventListener(
    'shopify:section:unload',
    event => {

      if (!state.layer) {
        return;
      }


      const originalParent =
        state.layer
          .__setlaroOriginalParent;


      if (
        originalParent &&
        event.target.contains(
          originalParent
        )
      ) {
        closeDrawer(
          false,
          false
        );
      }

    }
  );



  /* =========================================================
     PUBLIC API
  ========================================================== */

  window.SetlaroShippingReturns = {
    version: '2.0',

    open(trigger) {
      openDrawer(trigger);
    },

    close() {
      closeDrawer();
    }
  };

})();